"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const inflection = __importStar(require("inflection"));
const localStorage_1 = __importDefault(require("localStorage"));
const moment = require("moment");
const _1 = require(".");
const RealtimeAPI_1 = require("./RealtimeAPI");
class OMUniverse {
    constructor() {
        this.realtimeClient = new RealtimeAPI_1.OMRealtimeClient();
        this.environment = "localOnly";
        this.userType = _1.OMUser;
        /**
         * Application memory store: Redux store that acts as the memory cache of the application
         */
        this.cache = _1.ReduxMemoryCache.Store;
        this.getCookie = _1.LocalStorageCookie.getCookie;
        this.setCookie = _1.LocalStorageCookie.setCookie;
        this.removeCookie = _1.LocalStorageCookie.removeCookie;
        this.extendOrRenewSessionPromise = null;
        /**
         * Application persisted storage:
         * Uses browser local storage to keep a persisted cache of objects
         */
        this.persistedStoreName = "OMUniversePersistence";
        this.persistedObjectIDs = [];
        // // TODO:
        // private secureUUID(key: UniverseKey): UUID { }
        // private securePersist<T>(item?: T, key: UniverseKey) { }
        this.updateAndRestoreInstallation = () => __awaiter(this, void 0, void 0, function* () {
            const installationUuid = this.getCookie("installation");
            if (!installationUuid) {
                return;
            }
            this.installation = OMUniverse.shared.find(_1.Installation, installationUuid);
            if (!this.installation) {
                return;
            }
            this.installation.lastLaunchedAt = moment();
            return this.installation.save();
        });
        this.persistedLocalStorage = JSON.parse(localStorage_1.default.getItem(this.persistedStoreName) || "{}");
        this.restorePersistedObjectKeys();
    }
    get apiClient() {
        switch (this.environment) {
            case "localOnly":
            case "test":
                return new _1.OMLocalFileApiClient();
            case "development":
            case "staging":
            case "production":
                // TODO: remove hardcoded tenant
                return new _1.OMRestApiClient();
        }
    }
    get applicationKey() {
        return OMUniverse.applicationKey || "";
    }
    get baseURL() {
        return OMUniverse.baseURL || "";
    }
    static setApplicationKey(key) {
        OMUniverse.applicationKey = key;
    }
    static setBaseURL(baseURL) {
        OMUniverse.baseURL = baseURL;
    }
    isAuthenticated() {
        return this.user.identities && this.user.identities.length > 0;
    }
    changeEnvironment(env) {
        if (env === this.environment) {
            return;
        }
        this.environment = env;
    }
    objectType(anObject) {
        return inflection.underscore(anObject.constructor.Type);
    }
    touch(object, persist = false) {
        if (Array.isArray(object)) {
            object.forEach((each) => this.touch(each, persist));
        }
        else {
            this.cache.dispatch({ data: object, type: "insert" });
            if (persist) {
                this.persistObject(object);
            }
        }
    }
    release(object) {
        // Release from memory cache
        this.cache.dispatch({ data: object, type: "delete" });
        // Release from persisted local storage
        this.persistedLocalStorage[object.id] = undefined;
        localStorage_1.default.setItem(this.persistedStoreName, JSON.stringify(this.persistedLocalStorage));
    }
    find(ObjectType, identifier) {
        // Check if object exists in memory cache
        const cachedObjects = this.cache.getState();
        const cachedRawObject = cachedObjects[identifier] || this.restorePersistedObject(identifier);
        if (!cachedRawObject) {
            return undefined;
        }
        if (ObjectType.isType && !ObjectType.isType(cachedRawObject)) {
            return undefined;
        }
        return new ObjectType(cachedRawObject);
    }
    /**
     * Schedule session renewal if we have an existing session that is renewable
     * and if there's no upcoming renewal command scheduled yet
     */
    scheduleSessionRenewal() {
        if (!this.session || !this.session.isRenewable) {
            // tslint:disable-next-line
            console.log("scheduleSessionRenewal - bye");
            return;
        }
        const timeInterval = this.session.hasExpired ? 0 : this.session.refreshTimeIntervalInMilliSeconds;
        if (this.sessionRenewalTimer) {
            // tslint:disable-next-line
            console.log("scheduleSessionRenewal - clearTimeout");
            clearTimeout(this.sessionRenewalTimer);
        }
        this.sessionRenewalTimer = setTimeout(() => {
            // tslint:disable-next-line
            console.log("scheduleSessionRenewal - renewing");
            this.extendOrRenewSession()
                .then(() => {
                // tslint:disable-next-line
                console.log("scheduleSessionRenewal - renew completed");
                this.scheduleSessionRenewal();
            })
                .catch((error) => {
                // tslint:disable-next-line
                console.log("scheduleSessionRenewal - error");
                if (error.hasOwnProperty("response") && error.response !== undefined &&
                    (error.response.data.errors[0].title === "Session Blacklisted" ||
                        error.response.data.errors[0].detail === "jwt expired")) {
                    this.clearUserSession();
                    if (typeof document !== "undefined") {
                        document.location.reload();
                    }
                }
                throw error;
            });
        }, timeInterval);
    }
    restoreUserSession(userType) {
        return __awaiter(this, void 0, void 0, function* () {
            this.userType = userType;
            const user = this.getCookie("currentUser") && this.find(userType, this.getCookie("currentUser"));
            const installation = this.getCookie("installation") && this.find(_1.Installation, this.getCookie("installation"));
            const session = this.getCookie("session") && this.find(_1.OMSession, this.getCookie("session"));
            const identities = user && user.identities && user.identities.map((ref) => ref.actualObject);
            if (user && installation && session) {
                this.user = user;
                this.installation = installation;
                this.session = session;
                if (identities) {
                    this.identities = identities;
                }
            }
            else {
                const newUser = new userType({});
                newUser.ownerUuid = new _1.OMReference(_1.OMUser, newUser);
                this.setUser(newUser);
                const cachedInstallation = (() => {
                    const installationUuid = this.getCookie("installation");
                    if (!installationUuid) {
                        return;
                    }
                    return OMUniverse.shared.find(_1.Installation, installationUuid);
                })();
                const installationPromise = (() => __awaiter(this, void 0, void 0, function* () {
                    if (cachedInstallation) {
                        return this.updateAndRestoreInstallation();
                    }
                    else {
                        const newInstallation = _1.Installation.FromUser(newUser);
                        // We need to read modifiedAt from the server
                        // to account for timedrift
                        newInstallation.modifiedAt = undefined;
                        const requestTime = moment();
                        this.setInstallation(newInstallation);
                        const res = yield newInstallation.save();
                        const data = OMUniverse.shared.apiClient.objectsFromResponse(_1.Installation, res.data)[0];
                        const timeDriftInMilliseconds = requestTime.diff(data.modifiedAt);
                        // Set timedrift
                        _1.OMSession.timeDriftInMilliseconds = timeDriftInMilliseconds;
                        localStorage_1.default.setItem("timeDriftInMilliseconds", timeDriftInMilliseconds.toString());
                        return res;
                    }
                }))();
                return installationPromise
                    .then(() => {
                    // Create new anonymous user
                    return this.apiClient.send({
                        data: _1.JsonUtils.toJsonApi(newUser),
                        method: "POST",
                        url: userType.Path,
                    });
                })
                    .then((response) => {
                    const newSession = this.apiClient.objectsFromResponse(_1.OMSession, response.data)[0];
                    this.setSession(newSession);
                })
                    .catch((error) => {
                    this.release(this.installation);
                    throw error;
                });
            }
        });
    }
    signUp(identities) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user) {
                throw new Error("Not supported");
            }
            if (this.user.identities.length > 0) {
                this.clearUserSession();
                yield this.restoreUserSession(this.userType);
            }
            return this.addIdentities(identities, this.user);
        });
    }
    login(sessionType, identity) {
        return __awaiter(this, void 0, void 0, function* () {
            this.touch(identity);
            return this.apiClient.send({
                data: _1.JsonUtils.toJsonApi(identity),
                method: "POST",
                url: "sessions",
            })
                .then((response) => this.apiClient.objectsFromResponse(sessionType, response.data))
                .then((sessions) => sessions[0])
                .then((session) => {
                this.setSession(session);
                const user = session.user.actualObject;
                this.setUser(user);
                if (this.user.identities) {
                    this.identities = this.user.identities
                        .map((identityRef) => identityRef.actualObject)
                        .map((id) => new _1.OMIdentity(id));
                    this.identities.forEach((id) => this.touch(id, true));
                }
                if (this.user.groups) {
                    this.touch(this.user.groups.map((group) => group.actualObject), true);
                }
            })
                .catch((error) => {
                this.release(identity);
                throw error;
            });
        });
    }
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user || !this.session) {
                return;
            }
            this.realtimeClient.disconnectRealtime();
            try {
                yield this.apiClient.send({
                    method: "DELETE",
                    url: `sessions/${this.session.id}`,
                });
            }
            catch (error) {
                /* TODO */
            }
            this.user = undefined;
            this.session = undefined;
            this.identities = undefined;
            this.removeCookie("currentUser");
            this.removeCookie("session");
        });
    }
    setUser(user) {
        this.touch(user, true);
        this.setCookie("currentUser", user);
        this.user = user;
    }
    setSession(session) {
        this.touch(session, true);
        this.setCookie("session", session);
        this.session = session;
    }
    setInstallation(installation) {
        this.touch(installation, true);
        this.setCookie("installation", installation);
        this.installation = installation;
    }
    clearUserSession() {
        if (!this.user) {
            return;
        }
        if (this.user.identities === undefined) {
            return;
        }
        this.user = undefined;
        this.session = undefined;
        this.removeCookie("currentUser");
        this.removeCookie("session");
    }
    extendOrRenewSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session || !this.session.isRenewable) {
                return;
            }
            if (this.session.hasExpired) {
                return this.session.renew();
            }
            else {
                return this.session.extend();
            }
        });
    }
    /* REGION: Private */
    addIdentities(identities, user) {
        return __awaiter(this, void 0, void 0, function* () {
            const userRef = new _1.OMReference(_1.OMUser, user);
            user.identities = identities.map((identity) => new _1.OMReference(_1.OMIdentity, identity));
            identities.forEach((identity) => {
                identity.user = userRef;
                identity.ownerUuid = userRef;
            });
            return this.apiClient.send({
                data: _1.JsonUtils.toJsonApi(identities),
                method: "POST",
                params: { session: true },
                url: "identities",
            })
                .then((res) => {
                identities.map((each) => each.isInserted = true);
                OMUniverse.shared.touch(identities, true);
                this.setUser(user);
                return res;
            })
                .then((res) => {
                return this.apiClient.objectsFromResponse(_1.OMObject, res.data);
            })
                .catch((error) => {
                user.identities = [];
                throw error;
            });
        });
    }
    /**
     * Used by OMUniverse during initialization to find a list of all UUIDs of objects in localStorage
     */
    restorePersistedObjectKeys() {
        this.persistedObjectIDs = Object.keys(this.persistedLocalStorage);
    }
    /**
     *  Object Persistence keeps and restores objects between sessions, browser refresh, etc.
     */
    /**
     * Restore a single object from persisted storage that is instance of ObjectType
     * @param ObjectType Class of the ObjectType
     * @param identifier Identifier of the ObjectType
     */
    restorePersistedObject(identifier) {
        if (Object.keys(this.persistedLocalStorage).includes(identifier)) {
            return this.persistedLocalStorage[identifier];
        }
    }
    persistObject(anObject) {
        this.persistedLocalStorage[anObject.id] = anObject;
        // ROADMAP: This next line is horribly inefficient, because it causes a disk write at every object persist.
        // Try to do this only upon session end.
        localStorage_1.default.setItem(this.persistedStoreName, JSON.stringify(this.persistedLocalStorage));
    }
}
/* public set installation(value: Installation) {
    // // TODO:
    // if (!installation) {
    //     this.release(value)
    // }
} */
OMUniverse.shared = new OMUniverse();
exports.default = OMUniverse;
//# sourceMappingURL=OMUniverse.js.map